import type { IFilesystem } from '@livx.cc/wcli/core';
import type { AgentTool, ToolContext } from './tools';
import { formatCatalog } from './catalog';
import { splitFrontmatter, grabField } from './frontmatter';
import { expandEntry, type SiblingResolver } from './commands';

export interface SkillInfo {
  name: string;
  description: string;
  path: string;
}

/** Parse a SKILL.md's `name:` / `description:` frontmatter (falls back to scanning the head if undelimited). */
function parseFrontmatter(md: string): { name?: string; description?: string } {
  const { block } = splitFrontmatter(md);
  const b = block || md.slice(0, 600);
  return { name: grabField(b, 'name'), description: grabField(b, 'description') };
}

/**
 * Discover skills under `dir` (each `<dir>/<id>/SKILL.md`) and produce:
 *  - `catalog`: a names+descriptions block for the system prompt,
 *  - `tool`: a `Skill` tool that returns a skill's full SKILL.md (progressive disclosure).
 *
 * Skills are just VFS files — the Read tool already gives progressive disclosure; this
 * adds the catalog + a convenience tool. (Executable skills can be `bash`-exec'd: wcli
 * runs JS files from the VFS as commands.)
 */
/** Scan `dir`(s) for `<dir>/<id>/SKILL.md` entries (first dir wins on duplicate ids). */
export async function scanSkills(fs: IFilesystem, dir: string | string[]): Promise<SkillInfo[]> {
  const skills: SkillInfo[] = [];
  const seen = new Set<string>();
  for (const d of Array.isArray(dir) ? dir : [dir]) {
    if (!await fs.exists(d)) continue;
    for (const entry of await fs.readDir(d)) {
      if (seen.has(entry)) continue; // first dir wins (our .agent/ before .claude/)
      const skillMd = `${d}/${entry}/SKILL.md`;
      if (await fs.exists(skillMd)) {
        seen.add(entry);
        const fm = parseFrontmatter(await fs.readFile(skillMd));
        skills.push({ name: fm.name || entry, description: fm.description || '', path: skillMd });
      }
    }
  }
  return skills;
}

export async function loadSkills(
  fs: IFilesystem,
  dir: string | string[],
  opts: {
    /** @deprecated no-op — the catalog is full + task-independent (stable prompt prefix). */
    relevanceHint?: string;
    /** @deprecated no-op — use `budgetBytes`. */
    max?: number;
    /** Catalog byte budget (default 6000); over it descriptions trim, names never drop. */
    budgetBytes?: number;
    sibling?: SiblingResolver;
  } = {},
): Promise<{ skills: SkillInfo[]; catalog: string; tool?: AgentTool }> {
  const skills = await scanSkills(fs, dir);
  if (skills.length === 0) return { skills, catalog: '' };

  const catalog = formatCatalog(skills, {
    title: '## Skills (load one before acting on a matching task)',
    footer: 'When a task matches a skill, call the `Skill` tool with its name to load its full instructions.',
    budgetBytes: opts.budgetBytes,
  });

  const tool: AgentTool = {
    name: 'Skill',
    description: 'Load a skill (or slash command) by name — returns its full instructions (SKILL.md / expanded template), with any `args` substituted/appended. Resolves both the skill and command namespaces. Use when a task matches a listed skill or command.',
    parameters: { type: 'object', required: ['name'], properties: { name: { type: 'string' }, args: { type: 'string', description: 'arguments to fill the template' } } },
    async run({ name, args }, ctx: ToolContext) {
      const slug = String(name ?? '').replace(/^\//, '');
      let s = skills.find((x) => x.name === slug);
      let fresh: SkillInfo[] | undefined;
      if (!s) { // miss → rescan: skills created mid-conversation are loadable without a reprepare
        fresh = await scanSkills(ctx.fs, dir);
        s = fresh.find((x) => x.name === slug);
        if (s) skills.push(s);
      }
      if (s) return expandEntry(ctx.fs, s.path, String(args ?? '')); // skill: whole SKILL.md (frontmatter kept)
      // still a miss → merged namespace: try the sibling (commands) registry before failing.
      const sib = await opts.sibling?.(slug);
      if (sib) return expandEntry(ctx.fs, sib.path, String(args ?? ''), { stripFrontmatter: sib.stripFrontmatter });
      return `Error: no skill or command named '${slug}'. Available skills: ${(fresh ?? []).map((x) => x.name).join(', ') || '(none)'}`;
    },
  };
  return { skills, catalog, tool };
}
